import React, { useState, useRef, useEffect } from 'react';
import { Download, Mail, Loader, Database, Upload, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './Backup.css';

const Backup: React.FC = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'IT_ADMIN';
  const [recipientEmail, setRecipientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreStats, setRestoreStats] = useState<any>(null);
  const [dbInfo, setDbInfo] = useState<{ name: string; size: number; sizeFormatted: string } | null>(null);
  const [loadingDbInfo, setLoadingDbInfo] = useState(false);
  const [showDbExport, setShowDbExport] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/backup/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `password-wallet-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('Backup downloaded successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to download backup');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to download backup');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/backup/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recipientEmail }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Backup sent successfully to ${recipientEmail}. Summary: ${data.summary.totalCredentials} credentials, ${data.summary.totalShares} shares, ${data.summary.totalKubeconfigs} kubeconfigs`);
        setRecipientEmail('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || errorData.details || 'Failed to send backup via email');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send backup via email');
    } finally {
      setLoading(false);
    }
  };

  const handleFullBackupDownload = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/backup/full`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `password-wallet-full-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('Full backup downloaded successfully. This backup contains ALL data in the system.');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to download full backup');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to download full backup');
    } finally {
      setLoading(false);
    }
  };

  // Load database info when component mounts (if admin) - but only when user clicks to show export
  // We don't auto-load to avoid unnecessary queries

  const loadDatabaseInfo = async () => {
    setLoadingDbInfo(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/backup/database/info`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDbInfo(data.database);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load database information');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load database information');
    } finally {
      setLoadingDbInfo(false);
    }
  };

  const handleShowDbExport = () => {
    if (!dbInfo) {
      loadDatabaseInfo();
    }
    setShowDbExport(true);
  };

  const handleDatabaseBackup = async (format: 'sql' | 'custom') => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setShowDbExport(false); // Hide the info card when export starts

    try {
      const endpoint = format === 'sql' ? '/backup/database/sql' : '/backup/database/custom';
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = format === 'sql' ? 'sql' : 'dump';
        a.download = `password-wallet-database-${new Date().toISOString().split('T')[0]}.${extension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess(
          `Database ${format === 'sql' ? 'SQL' : 'custom format'} backup downloaded successfully. ` +
          `You can restore it using ${format === 'sql' ? 'psql' : 'pg_restore'} CLI command.`
        );
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to download database ${format} backup`);
      }
    } catch (error: any) {
      setError(error.message || `Failed to download database ${format} backup`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        setError('Please select a valid JSON backup file');
        return;
      }
      setRestoreFile(file);
      setError(null);
      setSuccess(null);
      setRestoreStats(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      setError('Please select a backup file first');
      return;
    }

    setRestoring(true);
    setError(null);
    setSuccess(null);
    setRestoreStats(null);

    try {
      // Read file content
      const fileContent = await restoreFile.text();
      const backupData = JSON.parse(fileContent);

      // Determine if it's a full backup or user backup
      const isFullBackup = backupData.metadata?.backupType === 'FULL_ADMIN_BACKUP';
      const endpoint = isFullBackup ? '/backup/full/restore' : '/backup/restore';

      // Check permissions for full backup
      if (isFullBackup && !isAdmin) {
        setError('This is a full system backup. Only administrators can restore it.');
        setRestoring(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: fileContent,
      });

      if (response.ok) {
        const data = await response.json();
        setRestoreStats(data.stats);
        setSuccess(data.message || 'Backup restored successfully!');
        setRestoreFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to restore backup';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          if (errorData.details && process.env.NODE_ENV === 'development') {
            console.error('Restore error details:', errorData.details);
          }
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || `Server error (${response.status})`;
        }
        setError(errorMessage);
        console.error('Restore failed:', {
          status: response.status,
          statusText: response.statusText,
          endpoint,
        });
      }
    } catch (error: any) {
      console.error('Restore error:', error);
      if (error instanceof SyntaxError) {
        setError('Invalid JSON file. Please ensure the file is a valid backup file.');
      } else {
        setError(error.message || 'Failed to restore backup. Please check the server logs.');
      }
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="backup-page">
      <div className="backup-header">
        <h1>Backup & Restore</h1>
        <p className="subtitle">Export and restore your credentials, shares, and kubeconfigs</p>
      </div>

      <div className="backup-options">
        {isAdmin && (
          <div className="backup-card admin-card">
            <div className="card-icon admin-icon">
              <Database size={32} />
            </div>
            <h2>Full System Backup (Admin Only)</h2>
            <p className="admin-description">
              <strong>⚠️ Administrator Access Required</strong><br />
              Download a complete backup of ALL data in the system including all users, credentials, kubeconfigs, VPNs, shares, audit logs, and sessions.
            </p>
            <button
              className="btn-primary admin-btn"
              onClick={handleFullBackupDownload}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={18} className="spinner" />
                  Downloading Full Backup...
                </>
              ) : (
                <>
                  <Database size={18} />
                  Download Full System Backup
                </>
              )}
            </button>
            <div className="admin-warning">
              <p><strong>Security Notice:</strong> This backup contains sensitive data from all users. Handle with extreme care.</p>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="backup-card admin-card" style={{ borderColor: '#10b981' }}>
            <div className="card-icon admin-icon" style={{ backgroundColor: '#10b98120' }}>
              <Database size={32} />
            </div>
            <h2>Database SQL Backup (Admin Only)</h2>
            <p className="admin-description">
              <strong>🗄️ PostgreSQL Database Export</strong><br />
              Export the entire PostgreSQL database as a SQL file. This includes <strong>ALL data</strong>:
              all users with passwords (passwordHash) and MFA secrets, all credentials, kubeconfigs, VPNs, shares, audit logs, and sessions.
              This can be restored directly using <code>psql</code> CLI command.
            </p>

            {!showDbExport ? (
              <button
                className="btn-primary"
                style={{ backgroundColor: '#10b981' }}
                onClick={handleShowDbExport}
                disabled={loadingDbInfo}
              >
                {loadingDbInfo ? (
                  <>
                    <Loader size={18} className="spinner" />
                    Loading Database Info...
                  </>
                ) : (
                  <>
                    <Database size={18} />
                    Show Database & Start Export
                  </>
                )}
              </button>
            ) : (
              <>
                {dbInfo ? (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px', 
                    backgroundColor: '#f0fdf4', 
                    border: '2px solid #10b981', 
                    borderRadius: '8px' 
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <Database size={20} style={{ color: '#10b981' }} />
                      <h3 style={{ margin: 0, color: '#065f46' }}>Database Information</h3>
                    </div>
                    <div style={{ fontSize: '14px', color: '#047857' }}>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Database Name:</strong> <code style={{ backgroundColor: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>{dbInfo.name}</code>
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Database Size:</strong> <code style={{ backgroundColor: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>{dbInfo.sizeFormatted}</code>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px', 
                    backgroundColor: '#fef3c7', 
                    border: '1px solid #f59e0b', 
                    borderRadius: '8px' 
                  }}>
                    <p style={{ margin: 0, color: '#92400e' }}>
                      {loadingDbInfo ? 'Loading database information...' : 'Failed to load database information. Click "Retry" to try again.'}
                    </p>
                    {!loadingDbInfo && (
                      <button
                        className="btn-primary"
                        style={{ marginTop: '10px', backgroundColor: '#f59e0b' }}
                        onClick={loadDatabaseInfo}
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className="btn-primary"
                    style={{ backgroundColor: '#10b981' }}
                    onClick={() => handleDatabaseBackup('sql')}
                    disabled={loading || !dbInfo}
                  >
                    {loading ? (
                      <>
                        <Loader size={18} className="spinner" />
                        Exporting SQL...
                      </>
                    ) : (
                      <>
                        <FileText size={18} />
                        Export SQL (.sql)
                      </>
                    )}
                  </button>
                  <button
                    className="btn-primary"
                    style={{ backgroundColor: '#6366f1' }}
                    onClick={() => handleDatabaseBackup('custom')}
                    disabled={loading || !dbInfo}
                  >
                    {loading ? (
                      <>
                        <Loader size={18} className="spinner" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Database size={18} />
                        Export Custom (.dump)
                      </>
                    )}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowDbExport(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
                <div className="admin-warning" style={{ marginTop: '15px', padding: '12px', backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '6px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#0c4a6e' }}>
                    <strong>📋 Restore Instructions:</strong><br />
                    <strong>SQL format:</strong> <code>psql -U username -d database_name -f backup.sql</code><br />
                    <strong>Custom format:</strong> <code>pg_restore -U username -d database_name backup.dump</code>
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        <div className="backup-card">
          <div className="card-icon">
            <Download size={32} />
          </div>
          <h2>Download My Backup</h2>
          <p>Download a complete backup of all your credentials, shares, and kubeconfigs as a JSON file.</p>
          <button
            className="btn-primary"
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader size={18} className="spinner" />
                Downloading...
              </>
            ) : (
              <>
                <Download size={18} />
                Download Backup
              </>
            )}
          </button>
        </div>

        <div className="backup-card">
          <div className="card-icon">
            <Mail size={32} />
          </div>
          <h2>Email Backup</h2>
          <p>Send a complete backup to an email address. The backup file will be attached to the email.</p>
          <form onSubmit={handleEmailBackup} className="email-form">
            <div className="form-group">
              <label htmlFor="recipientEmail">Recipient Email *</label>
              <input
                type="email"
                id="recipientEmail"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                required
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !recipientEmail}
            >
              {loading ? (
                <>
                  <Loader size={18} className="spinner" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail size={18} />
                  Send Backup via Email
                </>
              )}
            </button>
          </form>
        </div>

        <div className="backup-card restore-card">
          <div className="card-icon restore-icon">
            <Upload size={32} />
          </div>
          <h2>Restore Backup</h2>
          <p>Upload a backup file to restore your credentials, kubeconfigs, and VPNs. Existing items with the same name will be skipped.</p>
          <div className="restore-form">
            <div className="file-input-wrapper">
              <input
                ref={fileInputRef}
                type="file"
                id="backupFile"
                accept=".json,application/json"
                onChange={handleFileSelect}
                disabled={restoring}
                style={{ display: 'none' }}
              />
              <label htmlFor="backupFile" className="file-input-label">
                <FileText size={18} />
                {restoreFile ? restoreFile.name : 'Select Backup File'}
              </label>
            </div>
            {restoreFile && (
              <button
                className="btn-primary restore-btn"
                onClick={handleRestore}
                disabled={restoring}
              >
                {restoring ? (
                  <>
                    <Loader size={18} className="spinner" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Restore Backup
                  </>
                )}
              </button>
            )}
          </div>
          {isAdmin && (
            <div className="admin-note">
              <p><strong>Note:</strong> As an administrator, you can restore both user backups and full system backups.</p>
            </div>
          )}
        </div>
      </div>

      {restoreStats && (
        <div className="restore-stats">
          <h3>Restore Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Credentials:</span>
              <span className="stat-value">
                {restoreStats.credentialsCreated} created, {restoreStats.credentialsSkipped} skipped
                {restoreStats.credentialsErrors > 0 && `, ${restoreStats.credentialsErrors} errors`}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Kubeconfigs:</span>
              <span className="stat-value">
                {restoreStats.kubeconfigsCreated} created, {restoreStats.kubeconfigsSkipped} skipped
                {restoreStats.kubeconfigsErrors > 0 && `, ${restoreStats.kubeconfigsErrors} errors`}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">VPNs:</span>
              <span className="stat-value">
                {restoreStats.vpnsCreated} created, {restoreStats.vpnsSkipped} skipped
                {restoreStats.vpnsErrors > 0 && `, ${restoreStats.vpnsErrors} errors`}
              </span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div className="alert success">
          <strong>Success:</strong> {success}
        </div>
      )}

      <div className="backup-info">
        <h3>What's included in the backup?</h3>
        <ul>
          <li>All credentials you have access to (with decrypted data)</li>
          <li>All credential shares you created</li>
          <li>All kubeconfigs you have access to (metadata only, not decrypted content)</li>
          <li>Metadata including creation dates, permissions, and tags</li>
        </ul>

        <h3>Security Notice</h3>
        <div className="security-warning">
          <p>
            <strong>⚠️ Important:</strong> The backup file contains sensitive credential information.
            Please ensure you:
          </p>
          <ul>
            <li>Store backups in a secure location</li>
            <li>Use encryption when storing backups</li>
            <li>Do not share backup files with unauthorized persons</li>
            <li>Delete backup files if they were created by mistake</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Backup;

